import { useState, useEffect, useMemo } from "react";
import { Search, Plus, X, Loader2, Package, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * QuickAddPantryDialog — vis alle tidlegare kjøpte varer (frå globalt katalog +
 * pantry-historikk for denne båten), sortert etter popularitet. Kvar vare har
 * ein "+"-knapp som legg vara direkte inn i beholdningen med mengd 1 stk.
 *
 * Dialogen held seg open etter å ha lagt til, slik at brukaren raskt kan
 * legge til fleire varer i rekkefølgje.
 */
export default function QuickAddPantryDialog({ open, onOpenChange, defaultLocationId = "", onAdded }) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [addingKey, setAddingKey] = useState(null); // id being added right now
  const [addedKeys, setAddedKeys] = useState({}); // { [id]: timestamp } — recently added indicator

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    api.getCatalog({ sort: "popular", limit: 200 })
      .then((res) => { if (mounted) setCatalog(res.data || []); })
      .catch(() => { if (mounted) setCatalog([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return catalog;
    const q = query.trim().toLowerCase();
    return catalog.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [catalog, query]);

  const handleAdd = async (item) => {
    setAddingKey(item.id);
    try {
      await api.createPantryItem({
        name: item.name,
        amount: "1",
        unit: "stk",
        category: item.category || "Annet",
        location_id: defaultLocationId || "",
        in_stock: true,
      });
      setAddedKeys((p) => ({ ...p, [item.id]: Date.now() }));
      toast.success(`${item.name} lagt til`, { duration: 1500 });
      if (onAdded) onAdded(item);
      // Auto-clear the "added" flag after 3 seconds so user can add again
      setTimeout(() => {
        setAddedKeys((p) => {
          const next = { ...p };
          delete next[item.id];
          return next;
        });
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikkje leggja til vare");
    } finally {
      setAddingKey(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0" data-testid="quick-add-dialog">
        <DialogHeader className="p-4 pb-2 border-b border-stone-100">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Sparkles className="h-5 w-5 text-violet-600" />
            Hurtiglegg til beholdning
          </DialogTitle>
          <DialogDescription>
            Trykk <strong>+</strong> for å leggja til vara med mengd 1 stk. Dialogen held seg open så du kan leggja til fleire i rekkefølgje.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="p-4 pb-2 border-b border-stone-100 relative">
          <Search className="absolute left-7 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Søk i tidlegare kjøpte varer…"
            className="w-full rounded-lg border border-stone-200 bg-white pl-9 pr-9 py-2.5 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
            data-testid="quick-add-search"
            autoFocus
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-7 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              aria-label="Nullstill søk"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Item grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-stone-400">
              <Package className="h-10 w-10 mx-auto mb-3 text-stone-300" />
              <p className="text-sm">
                {query.trim()
                  ? `Ingen treff for "${query}"`
                  : "Ingen tidlegare kjøpte varer enno. Legg til nokre via 'Legg til vare' fyrst."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="quick-add-grid">
              {filtered.map((item) => {
                const isAdding = addingKey === item.id;
                const isAdded = !!addedKeys[item.id];
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 rounded-lg border ${isAdded ? "border-emerald-300 bg-emerald-50" : "border-stone-200 bg-white hover:bg-stone-50"} p-2.5 transition-colors`}
                    data-testid={`quick-add-item-${item.id}`}
                  >
                    {/* Product image / thumbnail */}
                    {item.image_url ? (
                      <img src={item.image_url} alt="" className="h-10 w-10 rounded object-cover flex-shrink-0 border border-stone-100" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-stone-100 flex items-center justify-center flex-shrink-0">
                        <Package className="h-4 w-4 text-stone-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">{item.name}</p>
                      <p className="text-[11px] text-stone-500">
                        {item.category || "Annet"}
                        {item.count > 0 && <> · <span className="text-stone-400">kjøpt {item.count}×</span></>}
                      </p>
                    </div>
                    <button
                      onClick={() => handleAdd(item)}
                      disabled={isAdding}
                      className={`h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                        isAdded
                          ? "bg-emerald-500 text-white"
                          : "bg-navy-800 text-white hover:bg-navy-900 disabled:opacity-60"
                      }`}
                      data-testid={`quick-add-btn-${item.id}`}
                      title="Legg til i beholdning (mengd 1 stk)"
                      aria-label={`Legg til ${item.name}`}
                    >
                      {isAdding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
